/* eslint-disable no-nested-ternary */
import React, { FC, useEffect, useState } from 'react';

import { matchMediaQuery } from '@crehana/utils';
import { useMe } from '@crehana/web/dist/ReactContexts/AuthContext';

import Loadable from 'Shared/Loadable';

import type { CoursesFragment } from 'Jsx/Shared/CourseCardV3/graphql/types/CoursesFragment';
import type { CourseRecommendedFragment } from 'Jsx/Shared/CourseCardV3/graphql/types/CourseRecommendedFragment';
import CourseList from 'Jsx/Catalog/components/CourseList/CourseList';
import CourseListPremium from 'Jsx/Catalog/components/CourseList/CourseListPremium';
import { CourseCardWrapper } from 'Jsx/Catalog/ui/CourseCardWrapper';

import { CourseCardSkeleton } from '../../../CourseCardV3/skeletons';

const itemsPerRow = {
  1680: 4,
  1440: 3,
  1280: 3,
  1024: 3,
};

const Loader = (
  <div className="flex overflow-hidden flex-nowrap">
    {Array.from({ length: 4 }).map((_, i) => (
      <CourseCardWrapper itemsPerRow={itemsPerRow} key={i.toString()} onRow>
        <CourseCardSkeleton className="pt-12" />
      </CourseCardWrapper>
    ))}
  </div>
);

interface CourseListSliderProps {
  loading?: boolean;
  title: string;
  courses: (CoursesFragment | CourseRecommendedFragment)[];
  link?: string;
  linkText?: string;
  hasMainTitle?: boolean;
  withPriority?: boolean;
  onClickCourse?: (course: CoursesFragment) => void;
}

const CourseListSlider: FC<CourseListSliderProps> = ({
  loading,
  title,
  courses,
  link,
  linkText,
  hasMainTitle,
  withPriority,
  onClickCourse,
}) => {
  const { loading: loadingMe, me } = useMe();
  const [showSlider, setShowSlider] = useState(false);
  const isPremium = me?.isPremiumMember;

  useEffect(() => {
    setShowSlider(matchMediaQuery('sm').matches);
  }, []);

  if (!loading && courses.length === 0) {
    return null;
  }

  const paginationSelector = title.replace(/\s/g, '');

  return (
    <div>
      <div className="flex items-center justify-between mb-24">
        <div className="flex items-center">
          {hasMainTitle ? (
            <h1 className="mr-16 font-h4">{title}</h1>
          ) : (
            <span className="mr-16 font-h4">{title}</span>
          )}

          {link && linkText && (
            <div className="hidden sm:block">
              <a
                href={link}
                className="tw-font-semibold uppercase text-12 text-secondary-main"
              >
                {linkText}
              </a>
            </div>
          )}
        </div>

        <div className={paginationSelector} />
      </div>
      {loading || loadingMe ? (
        Loader
      ) : showSlider ? (
        <Loadable
          module={() =>
            import(
              /* webpackChunkName: "catalog-course-list-slider" */ './Slider'
            )
          }
          paginationSelector={`.${paginationSelector}`}
          courses={courses}
          Loader={Loader}
          onClickCourse={onClickCourse}
        />
      ) : (
        <React.Fragment>
          {isPremium ? (
            <CourseListPremium
              skeletonItems={4}
              courses={courses.slice(0, 4)}
              loading={loading}
              showVisualRest={false}
            />
          ) : (
            <CourseList
              skeletonItems={4}
              courses={courses.slice(0, 4)}
              loading={loading}
              isMobile
              withPriority={withPriority}
              onClickCourse={onClickCourse}
            />
          )}

          {link && linkText && (
            <a
              href={link}
              className="flex justify-center w-full py-8 mt-24 font-caption text-secondary-main rounded-5"
            >
              {linkText}
            </a>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default CourseListSlider;
